import React from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import DateOfBirthInput from "../inputs/DateOfBirthInput";
import TextInput from "../inputs/TextInput";
import { useSchoolFee } from "../../context/schoolFeeContext";
import FormSection from "./FormSection";
import useIdentityMessages from "./useIdentityMessages";

/**
 * Journey B's identity step: the payer tells us who the student is, because
 * this school's roster is not searchable.
 *
 * ⚠ Presented as a DECLARATION, not an authentication. Nothing on this screen
 * proves the payer knows the student; the server records what they typed as
 * payer-asserted and the school reconciles it. The copy says so, so a payer
 * does not read a completed form as a confirmed match.
 *
 * ⛔ The date of birth is MANDATORY (M3) and the journey context does not gate
 * it -- `canEnter` treats a placement as identified. This form is the client
 * side of `CPS-422-109`, and the Continue button is what it withholds.
 */
const ManualIdentityForm = () => {
  const { t } = useTranslation();
  const {
    selectionId,
    matricule,
    setMatricule,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    dateOfBirth,
    setDateOfBirth,
  } = useSchoolFee();

  // Shown only once there is something wrong to say. An empty required field
  // on a form the payer has not filled in yet is not an error, it is an
  // unfinished form -- and the disabled Continue already says so.
  const messages = useIdentityMessages({
    matricule,
    firstName,
    lastName,
    dateOfBirth,
  });

  if (selectionId.trim() === "") return null;

  return (
    <Box data-testid="school-fee-manual-identity">
      <FormSection title={t("schoolfee.identity.title")}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }} mb={2}>
          {t("schoolfee.identity.hint")}
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextInput
              fullWidth
              required
              type="text"
              id="schoolfee-identity-matricule"
              label={t("schoolfee.identity.matricule_label")}
              value={matricule}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setMatricule(event.target.value)
              }
              {...messages.matricule}
              variant="outlined"
            />
          </Grid>

          {/* Two inputs, per the widget registry's CUSNAME rule: the pay contract
              carries first_name and last_name separately and splitting one box on
              whitespace is wrong for most Cameroonian names. */}
          <Grid item xs={12} md={6}>
            <TextInput
              fullWidth
              required
              type="text"
              id="schoolfee-identity-first-name"
              label={t("schoolfee.student.first_name")}
              value={firstName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFirstName(event.target.value)
              }
              variant="outlined"
              {...messages.firstName}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextInput
              fullWidth
              required
              type="text"
              id="schoolfee-identity-last-name"
              label={t("schoolfee.student.last_name")}
              value={lastName}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setLastName(event.target.value)
              }
              variant="outlined"
              {...messages.lastName}
            />
          </Grid>
          <Grid item xs={12}>
            <DateOfBirthInput
              required
              id="schoolfee-identity-dob"
              label={t("schoolfee.student.date_of_birth")}
              value={dateOfBirth}
              onChange={setDateOfBirth}
              {...messages.dateOfBirth}
            />
          </Grid>
        </Grid>
      </FormSection>
    </Box>
  );
};

export default ManualIdentityForm;
